import React, { useState } from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { formatCurrency } from '../utils/calculations';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import SEO from '../components/SEO';

ChartJS.register(ArcElement, Tooltip, Legend);

const ELSSCalculator = () => {
  const [monthlyInvestment, setMonthlyInvestment] = useState(12500);
  const [investmentPeriod, setInvestmentPeriod] = useState(15);
  const [expectedReturn, setExpectedReturn] = useState(12);
  const [taxBracket, setTaxBracket] = useState(30);
  const [result, setResult] = useState(null);

  const calculate = () => {
    const monthly = Number(monthlyInvestment) || 0;
    const period = Number(investmentPeriod) || 0;
    const returnRate = Number(expectedReturn) || 0;
    
    const monthlyReturn = returnRate / 12 / 100;
    const months = period * 12;
    
    // SIP Future Value
    const futureValue = monthly * ((Math.pow(1 + monthlyReturn, months) - 1) / monthlyReturn) * (1 + monthlyReturn);
    
    const totalInvestment = monthly * months;
    const wealthGained = futureValue - totalInvestment;
    
    // Tax savings (max 1.5L per year under 80C)
    const yearlyInvestment = monthly * 12;
    const eligibleDeduction = Math.min(yearlyInvestment, 150000);
    const yearlyTaxSaved = (eligibleDeduction * taxBracket) / 100;
    const totalTaxSaved = yearlyTaxSaved * period;
    
    // LTCG Tax on redemption (assuming all gains are LTCG after 3 years lock-in)
    const ltcgExemption = 125000;
    const taxableGain = Math.max(0, wealthGained - ltcgExemption);
    const ltcgTax = taxableGain * 0.125; // 12.5% LTCG
    
    // Net returns
    const netFutureValue = futureValue - ltcgTax;
    const effectiveReturn = ((Math.pow(netFutureValue / totalInvestment, 1 / period) - 1) * 100);
    
    // With tax savings reinvested
    const effectiveInvestment = totalInvestment - totalTaxSaved;
    const effectiveReturnWithTax = ((Math.pow(netFutureValue / effectiveInvestment, 1 / period) - 1) * 100);

    setResult({
      monthlyInvestment: monthly,
      totalInvestment,
      futureValue,
      wealthGained,
      yearlyTaxSaved,
      totalTaxSaved,
      ltcgTax,
      netFutureValue,
      effectiveReturn,
      effectiveReturnWithTax,
      eligibleDeduction,
    });
  };

  const formatCurrencyForPDF = (amount) => 'Rs. ' + Math.round(amount).toLocaleString('en-IN');

  const downloadPDF = () => {
    if (!result) return;
    const doc = new jsPDF();
    
    doc.setFontSize(18);
    doc.text('ELSS Tax Saver Calculator', 105, 20, { align: 'center' });
    
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString('en-IN')}`, 105, 28, { align: 'center' });

    autoTable(doc, {
      startY: 40,
      head: [['Parameter', 'Value']],
      body: [
        ['Monthly SIP', formatCurrencyForPDF(result.monthlyInvestment)],
        ['Investment Period', `${investmentPeriod} years`],
        ['Expected Return', `${expectedReturn}% p.a.`],
        ['Tax Bracket', `${taxBracket}%`],
        ['Total Investment', formatCurrencyForPDF(result.totalInvestment)],
        ['Future Value', formatCurrencyForPDF(result.futureValue)],
        ['Wealth Gained', formatCurrencyForPDF(result.wealthGained)],
        ['Yearly Tax Savings', formatCurrencyForPDF(result.yearlyTaxSaved)],
        ['Total Tax Savings (80C)', formatCurrencyForPDF(result.totalTaxSaved)],
        ['LTCG Tax on Redemption', formatCurrencyForPDF(result.ltcgTax)],
        ['Net Future Value', formatCurrencyForPDF(result.netFutureValue)],
      ],
      theme: 'striped',
      headStyles: { fillColor: [37, 99, 235] },
    });

    doc.save('ELSS_Calculator.pdf');
  };

  const chartData = result ? {
    labels: ['Investment', 'Tax Savings', 'Net Gains'],
    datasets: [{
      data: [result.totalInvestment, result.totalTaxSaved, result.wealthGained - result.ltcgTax],
      backgroundColor: ['#3b82f6', '#10b981', '#f59e0b'],
      borderWidth: 0,
    }]
  } : null;

  return (
    <div className="calculator-page">
      <SEO 
        title="ELSS Calculator"
        description="Free ELSS Calculator - Calculate Equity Linked Savings Scheme returns. Tax saving under 80C."
        keywords="ELSS calculator, ELSS tax saving calculator, equity linked savings scheme, ELSS returns calculator, 80C ELSS calculator"
        canonicalPath="/elss"
      />
      <div className="calculator-header">
        <h1>📊 ELSS Calculator</h1>
        <p>Equity Linked Savings Scheme - Tax saving with wealth creation</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Monthly SIP Amount (₹)</label>
            <input
              type="number"
              value={monthlyInvestment === '' ? '' : monthlyInvestment}
              onChange={(e) => setMonthlyInvestment(e.target.value === '' ? '' : Number(e.target.value))}
            />
            <small style={{ color: '#64748b', fontSize: '12px' }}>Max ₹12,500/month for full 80C benefit</small>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="input-group">
              <label>Investment Period (Years)</label>
              <input
                type="number"
                value={investmentPeriod === '' ? '' : investmentPeriod}
                onChange={(e) => setInvestmentPeriod(e.target.value === '' ? '' : Number(e.target.value))}
                min="3"
              />
              <small style={{ color: '#64748b', fontSize: '12px' }}>Min 3 years lock-in</small>
            </div>
            <div className="input-group">
              <label>Expected Return (% p.a.)</label>
              <input
                type="number"
                step="0.5"
                value={expectedReturn === '' ? '' : expectedReturn}
                onChange={(e) => setExpectedReturn(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
          </div>

          <div className="input-group">
            <label>Your Tax Bracket</label>
            <select value={taxBracket} onChange={(e) => setTaxBracket(Number(e.target.value))}>
              <option value={0}>0% (No Tax)</option>
              <option value={5}>5%</option>
              <option value={10}>10%</option>
              <option value={15}>15%</option>
              <option value={20}>20%</option>
              <option value={30}>30%</option>
            </select>
          </div>

          <button className="btn btn-primary" style={{ width: '100%', marginTop: '10px' }} onClick={calculate}>
            Calculate Returns
          </button>
        </div>

        <div className="calculator-results">
          {result ? (
            <>
              <div className="result-summary" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
                <h3>Net Future Value</h3>
                <div className="amount">{formatCurrency(result.netFutureValue)}</div>
                <p style={{ opacity: 0.9, marginTop: '8px' }}>
                  Effective Return: {result.effectiveReturnWithTax.toFixed(1)}% p.a. (with tax savings)
                </p>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Total Investment</span>
                  <span className="value">{formatCurrency(result.totalInvestment)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Future Value</span>
                  <span className="value">{formatCurrency(result.futureValue)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Wealth Gained</span>
                  <span className="value" style={{ color: '#10b981' }}>{formatCurrency(result.wealthGained)}</span>
                </div>
                <div className="result-item" style={{ background: '#ecfdf5' }}>
                  <span className="label">80C Deduction (Yearly)</span>
                  <span className="value">{formatCurrency(result.eligibleDeduction)}</span>
                </div>
                <div className="result-item" style={{ background: '#ecfdf5' }}>
                  <span className="label">Tax Saved (Yearly)</span>
                  <span className="value" style={{ color: '#10b981' }}>{formatCurrency(result.yearlyTaxSaved)}</span>
                </div>
                <div className="result-item" style={{ background: '#ecfdf5' }}>
                  <span className="label">Total Tax Saved ({investmentPeriod} yrs)</span>
                  <span className="value" style={{ color: '#10b981', fontWeight: '600' }}>{formatCurrency(result.totalTaxSaved)}</span>
                </div>
                <div className="result-item" style={{ background: '#fef2f2' }}>
                  <span className="label">LTCG Tax on Redemption</span>
                  <span className="value" style={{ color: '#ef4444' }}>{formatCurrency(result.ltcgTax)}</span>
                </div>
              </div>

              {chartData && (
                <div className="chart-container">
                  <h4>Returns Breakdown</h4>
                  <Pie data={chartData} options={{ responsive: true, plugins: { legend: { position: 'bottom' } } }} />
                </div>
              )}

              <button className="btn btn-secondary" style={{ width: '100%', marginTop: '20px' }} onClick={downloadPDF}>
                Download PDF Report
              </button>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
              <p>Enter your investment details to calculate ELSS returns</p>
            </div>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>💡 ELSS Benefits</h4>
        <ul>
          <li>Shortest lock-in (3 years) among 80C options</li>
          <li>Tax deduction up to ₹1.5 lakh under Section 80C</li>
          <li>Potential for higher returns (12-15% long-term average)</li>
          <li>LTCG above ₹1.25L taxed at 12.5% (Budget 2024)</li>
          <li>No limit on maximum investment</li>
        </ul>
      </div>
    </div>
  );
};

export default ELSSCalculator;
